import React, { useState, useEffect } from "react";
import Circle from "../../../assets/icons/Circle/Circle";
import { validateEmail } from "../../../helpers/utilities";
import Icon from "../Icon/Icon";
import Input from "../Input/Input";
import "./InviteCom.scss"
import { useDispatch, useSelector } from "react-redux";
import { fetchSendInvite } from "../../Reducers/actions/userActions";
import { updateUser } from "../../Reducers/actions/authActions";
import { useKeyboardDetectionNoRoot } from "../../../helpers/hooks";
import { getUserDisplayName } from "../../../helpers/userDisplayName";
import { MAX_INVITES, INVITE_PATH } from "../../constants/invitations";
import useSocialNetworksPrompt from "../../hooks/useSocialNetworksPrompt";
import SocialNetworksPrompt from "../SocialNetworksPrompt/SocialNetworksPrompt";

const BASE_URL = import.meta.env.VITE_SUPABASE_REDIRECT_URL

const InviteCom = () => {
    const { user } = useSelector((state) => ({
        user: state.auth.user
    }))
    const dispatch = useDispatch()

    const [email, setEmail] = useState("")
    const [errorMessage, setErrorMessage] = useState(null)
    const [loading, setLoading] = useState(false)
    const [isUserLoaded, setIsUserLoaded] = useState(false)
    const [copySuccess, setCopySuccess] = useState(false)

    const { keyboardVisible } = useKeyboardDetectionNoRoot();

    // Social networks prompt modal (share button clicked trigger - immediate)
    const socialNetworksPrompt = useSocialNetworksPrompt('share');

    useEffect(() => {
        if (user) {
            setIsUserLoaded(true)
        }
    }, [user])

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!validateEmail(email)) {
            setErrorMessage("Enter a valid email");
        } else {
            setErrorMessage(null);
            setLoading(true)
            fetchSendInvite({ email, sender_id: user?.user_id, name: getUserDisplayName(user) }).then(({ success, message }) => {
                if (success) {
                    setEmail("")
                    dispatch(updateUser({ nbInvites: user?.nbInvites + 1 }))
                } else {
                    setErrorMessage(message)
                }
                setLoading(false)
            })
        }
    }
    const inviteLink = user?.userName 
        ? `${BASE_URL}${INVITE_PATH}?code=${encodeURIComponent(user.userName)}`
        : '';

    const handleShareLink = async () => {
        // Check if social networks prompt should be shown
        if (socialNetworksPrompt.shouldShow) {
            socialNetworksPrompt.openModal();
        }

        if (navigator.share) {
            try {
                const shareTitle = "Join me on HYK 🎨";
                const shareText = `🎨 Discover and share amazing art!
                    Join me on HYK and explore a world of creativity.
                👉 ${inviteLink}`;

                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: inviteLink,
                });

            } catch (err) {
                console.error("Error sharing:", err);
            }
        }
        else {
            alert("Sharing is not supported on this device.");
        }
    }
    const handleCopyLink = async () => {
        if (!inviteLink) {
            alert("Please set a username before copying your invite link.");
            return;
        }
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
        } catch (err) {
            console.error('Failed to copy link:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = inviteLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    }

    const remainingInvites = isUserLoaded ? MAX_INVITES - (user?.nbInvites ?? 0) : null

    return (
        <div className="invite-container">
            {socialNetworksPrompt.isOpen && (
                <SocialNetworksPrompt 
                    open={socialNetworksPrompt.isOpen} 
                    onClose={socialNetworksPrompt.closeModal} 
                />
            )}
            <Icon className={'mt-40 mb-40'} alias={"invite"} width={keyboardVisible ? 80 : 200} height={keyboardVisible ? 80 : 200} color="#FF3232" />
            <p className="mt-0 mb-16 fg-crea fw-bold fs-sm2">
                {isUserLoaded ? `You have ${remainingInvites} invites left` : 'Loading invites...'}
            </p>
            <p className="mt-0 mb-32 fw-medium fs-regular fg-dark ">Share your personal link or enter email addresses below to invite people to join you on HYK.</p>
            
            {!keyboardVisible && <div className="comm-link" onClick={handleCopyLink}>
                <p className="link-text">{inviteLink}</p>
                <Icon
                    alias={'share_android'}
                    width={24}
                    color={"#FF3232"}
                    onClick={handleShareLink}
                />
                <Icon
                    className={"ml-12"}
                    alias={copySuccess ? 'check' : 'copy_invite_link'}
                    width={24}
                    color={"none"}
                    stroke={"#FF3232"}
                    strokeWidth={2}
                />

            </div>
            }
            {copySuccess && (
                <p className="copy-success">Link copied to clipboard!</p>
            )}
            <form onSubmit={handleSubmit} className="auth-form mb-16">
                <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    className="input-main mb-32"
                    onChange={e => {
                        setEmail(e.target?.value);
                        if (errorMessage) setErrorMessage(null)
                    }}
                />
                {errorMessage &&
                    <p className="fg-red d-flex aic mb-16">
                        <Icon alias={"info"} width={20} color="#B91C1C" />
                        <span className="ml-4">
                            {errorMessage}
                        </span>
                    </p>}
                <button type="submit" className="btn red-btn btn-full d-flex aic jcc fw-medium-bold" disabled={!!errorMessage || !email?.length > 0 || loading || !isUserLoaded || user?.nbInvites >= MAX_INVITES}>{loading ? 'Sending ...' : 'Send invite'}</button>
            </form>
        </div>
    )
}

export default InviteCom